import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, X } from 'lucide-react';
import { usePremiereDeliveryStore } from '../hooks/usePremiereStream.js';
import { useTimezone } from '../hooks/useTimezone.js';
import { usePlayerStore } from '../lib/stores.js';
import { formatTime } from '../lib/time.js';
import styles from './PremiereBanner.module.css';

const AUTO_CONFIRM_SECONDS = 10;

export default function PremiereBanner() {
  const primary = usePremiereDeliveryStore((s) => s.primary);
  const alerts = usePremiereDeliveryStore((s) => s.alerts);
  const dismiss = usePremiereDeliveryStore((s) => s.dismiss);
  const selectChannel = usePlayerStore((s) => s.selectChannel);
  const tz = useTimezone();
  const navigate = useNavigate();

  const [seconds, setSeconds] = useState(AUTO_CONFIRM_SECONDS);
  const lastIdRef = useRef(null);

  // Reset countdown whenever a new primary arrives.
  useEffect(() => {
    if (!primary) return;
    if (primary.id !== lastIdRef.current) {
      lastIdRef.current = primary.id;
      setSeconds(primary.auto_switch ? AUTO_CONFIRM_SECONDS : 0);
    }
  }, [primary]);

  useEffect(() => {
    if (!primary || !primary.auto_switch || seconds <= 0) return undefined;
    const t = setTimeout(() => setSeconds((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [primary, seconds]);

  useEffect(() => {
    if (primary?.auto_switch && seconds === 0) {
      performSwitch(primary);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [primary, seconds]);

  function performSwitch(sub) {
    const ch = sub.premiere?.channel;
    if (ch) {
      selectChannel({
        id: ch.id,
        name: ch.name,
        slug: ch.slug,
        logo_url: ch.logo_url,
      });
      navigate('/');
    }
    dismiss();
  }

  if (!primary && (!alerts || alerts.length === 0)) return null;

  return (
    <div className={styles.stack}>
      {primary && (
        <div className={styles.banner}>
          <button className={styles.close} onClick={dismiss} aria-label="Dismiss">
            <X size={14} />
          </button>
          <div className={styles.head}>
            <Sparkles size={14} /> Premiere starting
          </div>
          <div className={styles.title}>{primary.premiere.title}</div>
          <div className={styles.meta}>
            {primary.premiere.channel?.name} · starts {formatTime(primary.premiere.starts_at, tz)}
          </div>
          {primary.auto_switch && seconds > 0 && (
            <div className={styles.countdown}>
              Switching automatically in <strong>{seconds}</strong>s
            </div>
          )}
          <div className={styles.actions}>
            <button className="btn primary" onClick={() => performSwitch(primary)}>
              Switch now
            </button>
            <button className="btn ghost" onClick={dismiss}>Not now</button>
          </div>
        </div>
      )}
      {alerts && alerts.length > 0 && alerts.map((sub) => (
        <div key={sub.id} className={`${styles.banner} ${styles.alert}`}>
          <div className={styles.head}><Sparkles size={14} /> Also starting now</div>
          <div className={styles.title}>{sub.premiere.title}</div>
          <div className={styles.meta}>
            {sub.premiere.channel?.name} · {formatTime(sub.premiere.starts_at, tz)}
          </div>
          <div className={styles.actions}>
            <button className="btn" onClick={() => {
              const ch = sub.premiere?.channel;
              if (ch) selectChannel({
                id: ch.id, name: ch.name, slug: ch.slug, logo_url: ch.logo_url,
              });
            }}>Switch instead</button>
          </div>
        </div>
      ))}
    </div>
  );
}

